/**
 * DUPLICATES AND SHOPS — the two admin screens that are mostly a query.
 *
 * DUPLICATES first, because it is the one that costs money. The same
 * customer entered twice means their history splits: half the jobs on one
 * record, half on the other, and neither tells the truth when somebody
 * rings up asking what you did last time. The old system had a finder and
 * this is the same idea with the matching made explicit.
 *
 * MATCHED ON THE PHONE, not the name. "Bob Smith" and "Robert Smith" are
 * one person and no string comparison will tell you that; a phone number
 * is the thing a customer actually has one of. `phone10` is generated in
 * the database precisely so this is a grouping rather than five kinds of
 * fuzzy match. Names are shown so a human can confirm — two people at one
 * household number really do exist, and the software should not merge them
 * behind your back.
 *
 * MERGING IS NOT BUILT. Finding them is safe; combining two customers
 * touches every table that references one and has to move jobs, vehicles,
 * invoices, photos and conversations without orphaning any of them. That
 * is a transaction in the database, not a loop in a browser, and doing it
 * badly is worse than not doing it. The finder is honest about that.
 */

import { db } from "../data/client.ts";
import { esc, ago, mount, since } from "../ui/dom.ts";
import type { Role } from "../domain/permissions.ts";

const BOSS: Role[] = ["admin", "supervisor"];

type Cust = {
  id: string; full_name: string; phone: string | null; phone10: string | null;
  email: string | null; created_at: string;
};

export function denied(what: string): void {
  mount(`<div class="page"><header class="page-head">
      <a class="back" href="#/more">&larr; Everything else</a><h1>${esc(what)}</h1></header>
    <div class="card pad"><p class="quiet">This one is kept to the owner and supervisors.</p>
    </div></div>`);
}

/* ── duplicates ───────────────────────────────────────────────────── */

/* The Duplicates page moved to src/pages/duplicates.ts on 2026-09-03.
   It grew from a list into three tabs with working merges, and the rules
   for "these two are the same" now live in the database where they can be
   tested. `denied()` below is still shared with it. */

/* ── shops ────────────────────────────────────────────────────────── */

/**
 * EVERY SHOP, AND WORKING IN ONE OF THEM.
 *
 * This page used to select from `accounts` directly, which meant it could
 * only ever show ONE shop: the policy on that table is
 * `id = current_account_id()`. The page carried a note explaining that row
 * level security would do this -- a bug described rather than fixed. It now
 * asks `list_shops()`, which is definer and can see past the policy for the
 * short allow-list of people who own the platform. Everyone else still gets
 * their own shop and nothing else.
 *
 * SWITCHING IS A REAL MOVE, not a filter. `profiles.id` is the auth user
 * id, so one login has exactly one profile and one `account_id`, and every
 * policy on every table reads it. Switching writes that field. While you
 * are away, your own shop's rows are genuinely invisible to you -- which is
 * the point, and why the banner is loud and the way home is always on
 * screen.
 */
export async function shopsPage(role: Role = "viewer"): Promise<void> {
  if (!BOSS.includes(role)) return denied("Shops");

  mount(`<div class="page">
    <header class="page-head"><a class="back" href="#/more">&larr; Everything else</a>
      <h1>Shops</h1></header>
    <div id="shops"><div class="loading">Reading&hellip;</div></div>
  </div>`);

  const host = document.getElementById("shops");
  if (!host) return;

  type Shop = {
    id: string; name: string; phone: string | null; email: string | null;
    address: string | null; labour_rate: number | null; plan: string | null;
    /* What they actually pay, when it is not the list price, and the
       gateway's own id for it. Manko was on a live $99 subscription while
       this table said free. See migration 0094. */
    plan_rate: number | null; plan_since: string | null; plan_ref: string | null;
    created_at: string; logins: number; never_in: number; jobs: number;
    is_home: boolean; is_current: boolean;
  };

  const draw = async (note = ""): Promise<void> => {
    const sb = await db();
    const { data, error } = await sb.rpc("list_shops");
    if (error) {
      host.innerHTML = `<div class="card"><div class="empty bad">${esc(error.message)}</div></div>`;
      return;
    }
    const shops = (data ?? []) as Shop[];
    const here = shops.find((s) => s.is_current);
    const home = shops.find((s) => s.is_home);
    const away = here && home && here.id !== home.id;

    host.innerHTML = `
      ${note ? `<p class="msg good">${esc(note)}</p>` : ""}
      ${away ? `
        <div class="card awaybar">
          <div class="card-head">You are working in ${esc(here!.name)}</div>
          <div class="pad">
            <p class="fine">Everything on every screen is ${esc(here!.name)}'s until you go back.
               Your own shop's jobs, money and customers are hidden while you are here.</p>
            <button class="btn primary wide" data-go="${esc(home!.id)}">Back to ${esc(home!.name)}</button>
          </div>
        </div>` : ""}
      <div class="card">
        <div class="card-head">${shops.length} ${shops.length === 1 ? "shop" : "shops"}</div>
        ${shops.map((s) => `
          <div class="row${s.is_current ? " rowhere" : ""}">
            <div class="row-main">
              <div class="row-title"><span class="num">${esc(s.name)}</span>
                ${s.is_current ? `<span class="pill s-scheduled">you are here</span>` : ""}
                ${s.is_home && !s.is_current ? `<span class="pill">home</span>` : ""}
                ${s.plan === "paid"
                  ? `<span class="pill q-approved">${s.plan_rate
                      ? `$${Number(s.plan_rate).toFixed(0)}/mo`
                      : "paid"}</span>` : ""}
                ${s.plan === "paid" && !s.plan_ref
                  ? `<span class="pill warn" title="No Authorize.net subscription id on this shop, so nothing here can be checked against the gateway">no subscription on file</span>` : ""}
                ${s.never_in > 0
                  ? `<span class="pill s-cancelled" title="Somebody here has a login and has never once used it. That is how Ron at Manko went fifteen days locked out without anybody knowing.">${s.never_in} never signed in</span>` : ""}</div>
              <div class="row-sub">${esc(s.address ?? s.email ?? s.phone ?? "nothing on file")}</div>
              <div class="row-note">${s.jobs} ${s.jobs === 1 ? "job" : "jobs"} &middot;
                ${s.logins} ${s.logins === 1 ? "login" : "logins"} &middot;
                since ${esc(since(s.created_at))}${
                s.plan === "paid" && s.plan_since
                  ? ` &middot; paying since ${esc(s.plan_since)}${
                      s.plan_ref ? ` &middot; ANet ${esc(s.plan_ref)}` : ""}` : ""}</div>
            </div>
            <div class="row-side">
              <div class="amount">${s.labour_rate ? `$${Number(s.labour_rate).toFixed(0)}/hr` : "—"}</div>
              ${s.is_current ? "" : `<button class="btn" data-go="${esc(s.id)}">Work in this shop</button>`}
            </div>
          </div>`).join("")}
      </div>`;

    host.querySelectorAll<HTMLButtonElement>("button[data-go]").forEach((b) =>
      b.addEventListener("click", async () => {
        const target = b.dataset["go"]!;
        const name = shops.find((s) => s.id === target)?.name ?? "that shop";
        // Asked out loud. This changes what every other screen is about,
        // and somebody who does it by accident will think the data is gone.
        if (!confirm(`Work in ${name}? Every screen will show ${name}'s data until you come back.`)) return;
        b.disabled = true; b.textContent = "Switching…";
        const { data: said, error: e2 } = await sb.rpc("switch_shop", { target });
        if (e2) {
          b.disabled = false; b.textContent = "Work in this shop";
          host.insertAdjacentHTML("afterbegin", `<p class="msg bad">${esc(e2.message)}</p>`);
          return;
        }
        await draw(String(said ?? ""));
      }));
  };

  try { await draw(); } catch (e) {
    host.innerHTML = `<div class="card"><div class="empty bad">${
      esc(e instanceof Error ? e.message : String(e))}</div></div>`;
  }
}
